import { useCallback } from "react";
import { IUser } from "@vmosolutions/oc-interfaces";
import { useAppUsersById } from "src/hooks/useAppUsersById";
import {AcknowledgedUser} from "./acknowledgementState";

const formatName = (user: IUser): string => `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "Unknown";

/**
 * Custom hook that returns a function to get the name of the user associated with an acknowledged event.
 * It uses the `useAppUsersById` hook to access the map of users by their ID, allowing it to resolve user
 * IDs to user objects and format their names for display.
 *
 * @returns {(acknowledgedUser: AcknowledgedUser) => string} A function that takes an acknowledged user 
 * and returns the formatted name of the user or "Unknown" if the user cannot be resolved. 
 */
export function useRetrieveUserName(): (acknowledgedUser: AcknowledgedUser) => string {
  const usersById = useAppUsersById();

  return useCallback(
    (acknowledged: AcknowledgedUser): string => {
      const user = acknowledged?.user;
      if (!user) return "Unknown";
      if (typeof user !== "string") return formatName(user);
      const resolved = usersById.get(user);
      return resolved ? formatName(resolved) : user;
    },
    [usersById]
  );
}
